export type PhoneRepairRegion = 'temecula' | 'north-county-sd';

export interface PhoneRepairArea {
  slug: string;
  city: string;
  state: string;
  county: string;
  region: PhoneRepairRegion;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  heroHeading: string;
  heroSubheading: string;
  localIntro: string;
  serviceNote: string;       // turnaround / appointment expectations
  turnaroundNote: string;    // shown prominently in hero/location section
  isSameDay: boolean;        // true = Temecula region (same-day emphasis)
  population: string;
  driveMinutes: string;
  nearbyLandmarks: string[];
  nearbyCities: string[];
  lat: number;
  lng: number;
}

export const PHONE_REPAIR_AREAS: PhoneRepairArea[] = [

  // ── Temecula Region (Primary — Same-Day Focus) ─────────────────────────────

  {
    slug: 'temecula',
    city: 'Temecula',
    state: 'CA',
    county: 'Riverside County',
    region: 'temecula',
    isSameDay: true,
    metaTitle: 'iPhone & Phone Repair in Temecula, CA | GameFix Solutions',
    metaDescription:
      'Premium iPhone, iPad, Samsung, and MacBook repair in Temecula, CA. Most repairs completed same day by appointment. High-quality parts, warranty-backed service. No bargain-bin components.',
    keywords: [
      'iphone repair temecula',
      'phone repair temecula ca',
      'ipad repair temecula',
      'samsung repair temecula',
      'macbook repair temecula',
      'screen replacement temecula',
      'battery replacement temecula',
      'iphone screen temecula',
    ],
    heroHeading: 'Premium iPhone & Phone Repair in Temecula, CA',
    heroSubheading:
      'Fast, quality-first repair for iPhones, iPads, Samsung devices, and MacBooks. Most repairs completed same day by appointment — no bargain components, no shortcuts.',
    localIntro:
      'GameFix Solutions is based in the Temecula Valley, making us the most convenient option for residents throughout Southwest Riverside County. Whether you\'re in Redhawk, Harveston, or anywhere in between, we\'re a short drive away for same-day iPhone screen replacements, battery service, and more.',
    serviceNote:
      'Temecula is our primary service location. We prioritize same-day turnaround for most iPhone and Samsung repairs when parts are in stock.',
    turnaroundNote: 'Same-day repairs available by appointment',
    population: '110,000+',
    driveMinutes: '0–15',
    nearbyLandmarks: ['Promenade Temecula', 'Old Town Temecula', 'Pechanga Resort', 'Wine Country'],
    nearbyCities: ['Murrieta', 'Wildomar', 'Menifee', 'Winchester'],
    lat: 33.4936,
    lng: -117.1484,
  },

  {
    slug: 'murrieta',
    city: 'Murrieta',
    state: 'CA',
    county: 'Riverside County',
    region: 'temecula',
    isSameDay: true,
    metaTitle: 'iPhone & Phone Repair Near Murrieta, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and iPad repair near Murrieta, CA. Just minutes from California Oaks. Same-day service by appointment, premium-quality parts, 90-day warranty.',
    keywords: [
      'iphone repair murrieta',
      'phone repair murrieta ca',
      'ipad repair murrieta',
      'samsung repair murrieta',
      'screen repair murrieta',
      'battery replacement murrieta',
    ],
    heroHeading: 'Premium iPhone & Phone Repair Near Murrieta, CA',
    heroSubheading:
      'Murrieta\'s closest professional phone repair service — just south in Temecula. Quality parts, same-day service by appointment, and a 90-day warranty on all repairs.',
    localIntro:
      'Murrieta residents are just 10–15 minutes from our Temecula location. We serve customers from California Oaks, Bear Creek, Greer Ranch, and across the city for iPhone screen replacements, battery service, and Samsung repairs.',
    serviceNote:
      'As a Murrieta-area customer, you\'re 10–15 minutes from same-day service at our Temecula location.',
    turnaroundNote: 'Same-day repairs available — 10-15 min from Murrieta',
    population: '120,000+',
    driveMinutes: '10–15',
    nearbyLandmarks: ['Town Square Murrieta', 'California Oaks Sports Park', 'Murrieta Hot Springs'],
    nearbyCities: ['Temecula', 'Wildomar', 'Menifee', 'Winchester'],
    lat: 33.5539,
    lng: -117.2139,
  },

  {
    slug: 'menifee',
    city: 'Menifee',
    state: 'CA',
    county: 'Riverside County',
    region: 'temecula',
    isSameDay: true,
    metaTitle: 'iPhone & Phone Repair Near Menifee, CA | GameFix Solutions',
    metaDescription:
      'Premium iPhone, Samsung, and iPad repair near Menifee, CA. About 20 minutes to our Temecula location. Same-day service by appointment, high-quality parts, 90-day warranty.',
    keywords: [
      'iphone repair menifee',
      'phone repair menifee ca',
      'samsung repair menifee',
      'ipad repair menifee',
      'screen replacement menifee',
      'battery replacement menifee',
    ],
    heroHeading: 'Premium iPhone & Phone Repair Near Menifee, CA',
    heroSubheading:
      'Menifee residents are about 20 minutes from professional-grade phone repair in Temecula. iPhone screen replacements, battery service, Samsung repairs — same-day by appointment.',
    localIntro:
      'Menifee is one of the fastest-growing cities in California, and residents in Heritage Lakes, Audie Murphy Ranch, and Romoland have easy access to our Temecula repair center via I-215.',
    serviceNote:
      'From most Menifee neighborhoods, you\'re about 20 minutes from same-day iPhone and Samsung repair at our Temecula location.',
    turnaroundNote: 'Same-day available — ~20 min from Menifee',
    population: '100,000+',
    driveMinutes: '20',
    nearbyLandmarks: ['Heritage Lakes', 'Audie Murphy Ranch', 'I-215 / Newport Rd'],
    nearbyCities: ['Temecula', 'Murrieta', 'Sun City', 'Winchester'],
    lat: 33.6971,
    lng: -117.1847,
  },

  {
    slug: 'winchester',
    city: 'Winchester',
    state: 'CA',
    county: 'Riverside County',
    region: 'temecula',
    isSameDay: true,
    metaTitle: 'iPhone & Phone Repair Near Winchester, CA | GameFix Solutions',
    metaDescription:
      'Quality iPhone, Samsung, and iPad repair near Winchester, CA. Approximately 15-20 minutes to our Temecula location. Same-day service available, premium parts, 90-day warranty.',
    keywords: [
      'iphone repair winchester ca',
      'phone repair winchester california',
      'samsung repair winchester',
      'screen replacement winchester',
      'battery replacement winchester ca',
    ],
    heroHeading: 'Premium iPhone & Phone Repair Near Winchester, CA',
    heroSubheading:
      'Winchester is about 15–20 minutes from our Temecula repair center. iPhone and Samsung repairs done same day by appointment — quality parts, no shortcuts.',
    localIntro:
      'Winchester residents in French Valley, Rancho Bella Vista, and surrounding areas are a short drive from professional phone repair in Temecula. We serve customers who need more than a mall kiosk repair.',
    serviceNote:
      'Most Winchester neighborhoods are 15–20 minutes from same-day service at our Temecula location via Winchester Road.',
    turnaroundNote: 'Same-day available — ~15-20 min from Winchester',
    population: '20,000+',
    driveMinutes: '15–20',
    nearbyLandmarks: ['French Valley Airport area', 'Rancho Bella Vista', 'Winchester Rd / I-15'],
    nearbyCities: ['Temecula', 'Murrieta', 'Menifee', 'Hemet'],
    lat: 33.6995,
    lng: -117.0864,
  },

  // ── North County San Diego (Secondary — Appointment-Based) ─────────────────

  {
    slug: 'oceanside',
    city: 'Oceanside',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in Oceanside, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and iPad repair available in Oceanside, CA. Appointment-based service — turnaround depends on scheduling and parts availability. Premium quality parts.',
    keywords: [
      'iphone repair oceanside',
      'phone repair oceanside ca',
      'samsung repair oceanside',
      'ipad repair oceanside',
      'screen replacement oceanside',
      'battery replacement oceanside',
    ],
    heroHeading: 'iPhone & Phone Repair in Oceanside, CA',
    heroSubheading:
      'Appointment-based iPhone, Samsung, and iPad repair serving Oceanside and North County San Diego. Quality-first service — not a mall kiosk.',
    localIntro:
      'We provide appointment-based phone repair service throughout Oceanside and North County San Diego. As a non-storefront operation, we schedule service on availability — not walk-in. This lets us focus on quality work rather than rushed volume repairs.',
    serviceNote:
      'Service in Oceanside is by appointment only. Turnaround time depends on scheduling availability and parts. Contact us to discuss your repair before booking.',
    turnaroundNote: 'Appointment-based — turnaround varies',
    population: '180,000+',
    driveMinutes: 'Serving North County SD directly',
    nearbyLandmarks: ['Camp Pendleton', 'Oceanside Pier', 'El Comal area', 'Tri-City Medical Center'],
    nearbyCities: ['Carlsbad', 'Vista', 'San Marcos', 'Escondido'],
    lat: 33.1959,
    lng: -117.3795,
  },

  {
    slug: 'carlsbad',
    city: 'Carlsbad',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in Carlsbad, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and iPad repair available in Carlsbad, CA. Appointment-based service throughout North County San Diego. High-quality parts and warranty-backed repairs.',
    keywords: [
      'iphone repair carlsbad',
      'phone repair carlsbad ca',
      'samsung repair carlsbad',
      'ipad repair carlsbad',
      'screen replacement carlsbad',
      'macbook repair carlsbad',
    ],
    heroHeading: 'iPhone & Phone Repair in Carlsbad, CA',
    heroSubheading:
      'Quality iPhone, Samsung, and MacBook repair serving Carlsbad by appointment. Not a volume shop — focused on doing it right.',
    localIntro:
      'We serve Carlsbad-area customers for phone repair, battery replacement, and MacBook service by appointment. Our non-storefront model means we\'re not rushed, and every repair gets the attention it deserves.',
    serviceNote:
      'Carlsbad service is appointment-based. Scheduling and parts availability determine turnaround. We\'ll be upfront about timing when you reach out.',
    turnaroundNote: 'Appointment-based — contact us to schedule',
    population: '120,000+',
    driveMinutes: 'Serving North County SD directly',
    nearbyLandmarks: ['The Forum Carlsbad', 'Legoland', 'Bressi Ranch', 'Palomar Airport area'],
    nearbyCities: ['Oceanside', 'Vista', 'San Marcos', 'Encinitas'],
    lat: 33.1581,
    lng: -117.3506,
  },

  {
    slug: 'vista',
    city: 'Vista',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in Vista, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and iPad repair in Vista, CA. Appointment-based service in North County San Diego. Quality parts, no bargain shortcuts. Contact us to schedule.',
    keywords: [
      'iphone repair vista ca',
      'phone repair vista california',
      'samsung repair vista',
      'ipad repair vista',
      'screen replacement vista ca',
    ],
    heroHeading: 'iPhone & Phone Repair in Vista, CA',
    heroSubheading:
      'Appointment-based phone repair serving Vista and North County San Diego. iPhone screens, batteries, Samsung, and more — done right.',
    localIntro:
      'Vista customers have access to quality phone repair service through our North County appointment-based model. We work with you on scheduling and set realistic turnaround expectations upfront.',
    serviceNote:
      'Vista service is appointment-based. Turnaround time varies depending on scheduling and parts availability. Reach out before booking to confirm.',
    turnaroundNote: 'Appointment-based — turnaround varies by service',
    population: '100,000+',
    driveMinutes: 'Serving North County SD directly',
    nearbyLandmarks: ['Moonlight Amphitheatre', 'Shadowridge area', 'Brengle Terrace Park'],
    nearbyCities: ['Oceanside', 'Carlsbad', 'San Marcos', 'Escondido'],
    lat: 33.2000,
    lng: -117.2425,
  },

  {
    slug: 'san-marcos',
    city: 'San Marcos',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in San Marcos, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and MacBook repair serving San Marcos, CA by appointment. Quality-first phone repair in North County San Diego — not the cheapest, but done right.',
    keywords: [
      'iphone repair san marcos ca',
      'phone repair san marcos california',
      'samsung repair san marcos',
      'macbook repair san marcos',
      'screen replacement san marcos',
    ],
    heroHeading: 'iPhone & Phone Repair in San Marcos, CA',
    heroSubheading:
      'Quality phone and MacBook repair serving San Marcos by appointment. We prioritize doing the job right over doing it cheapest.',
    localIntro:
      'San Marcos residents — including those near CSU San Marcos, Twin Oaks Valley, and the Highway 78 corridor — have access to professional-grade phone repair through our appointment-based North County service.',
    serviceNote:
      'San Marcos service is appointment-based. We\'ll work with your schedule and give you an honest assessment of turnaround before you commit.',
    turnaroundNote: 'Appointment-based — contact us first',
    population: '100,000+',
    driveMinutes: 'Serving North County SD directly',
    nearbyLandmarks: ['CSUSM Campus', 'Grand Ave district', 'Twin Oaks Valley', 'Hwy 78 corridor'],
    nearbyCities: ['Vista', 'Carlsbad', 'Escondido', 'Oceanside'],
    lat: 33.1434,
    lng: -117.1661,
  },

  {
    slug: 'escondido',
    city: 'Escondido',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in Escondido, CA | GameFix Solutions',
    metaDescription:
      'Premium iPhone, Samsung, and iPad repair available in Escondido, CA. Appointment-based service — quality parts, honest turnaround estimates, 90-day warranty.',
    keywords: [
      'iphone repair escondido',
      'phone repair escondido ca',
      'samsung repair escondido',
      'ipad repair escondido',
      'screen replacement escondido',
    ],
    heroHeading: 'iPhone & Phone Repair in Escondido, CA',
    heroSubheading:
      'Appointment-based phone repair serving Escondido and inland North County. iPhone, Samsung, iPad — quality work with honest turnaround expectations.',
    localIntro:
      'Escondido customers in Hidden Valley, Felicita, and the downtown corridor can access professional phone repair through our appointment-based North County model. We don\'t rush repairs — we do them right.',
    serviceNote:
      'Escondido service is by appointment. Turnaround depends on scheduling availability and parts. Contact us for a quick assessment before booking.',
    turnaroundNote: 'Appointment-based — turnaround varies',
    population: '155,000+',
    driveMinutes: 'Serving North County SD directly',
    nearbyLandmarks: ['California Center for the Arts', 'Grape Day Park', 'Hidden Valley Pkwy', 'Kit Carson Park'],
    nearbyCities: ['San Marcos', 'Vista', 'Rancho Bernardo', 'Valley Center'],
    lat: 33.1192,
    lng: -117.0864,
  },

  {
    slug: 'encinitas',
    city: 'Encinitas',
    state: 'CA',
    county: 'San Diego County',
    region: 'north-county-sd',
    isSameDay: false,
    metaTitle: 'iPhone & Phone Repair in Encinitas, CA | GameFix Solutions',
    metaDescription:
      'Professional iPhone, Samsung, and iPad repair in Encinitas, CA. Appointment-based service in coastal North County San Diego. Quality parts, honest estimates, no shortcuts.',
    keywords: [
      'iphone repair encinitas',
      'phone repair encinitas ca',
      'samsung repair encinitas',
      'screen replacement encinitas',
      'battery replacement encinitas',
    ],
    heroHeading: 'iPhone & Phone Repair in Encinitas, CA',
    heroSubheading:
      'Quality phone repair serving Encinitas and coastal North County by appointment. iPhone screens, batteries, Samsung — done right, not done cheap.',
    localIntro:
      'Encinitas customers in Leucadia, Olivenhain, and Cardiff-by-the-Sea have access to professional phone repair through our appointment-based North County service. We set expectations clearly and deliver quality work.',
    serviceNote:
      'Encinitas service is appointment-based. Contact us before booking so we can discuss your device, turnaround, and pricing honestly.',
    turnaroundNote: 'Appointment-based — contact us to arrange',
    population: '65,000+',
    driveMinutes: 'Serving coastal North County SD',
    nearbyLandmarks: ['Moonlight Beach', 'Leucadia area', 'Olivenhain', 'Cardiff-by-the-Sea'],
    nearbyCities: ['Carlsbad', 'Solana Beach', 'Del Mar', 'San Marcos'],
    lat: 33.0369,
    lng: -117.2919,
  },
];

export const getPhoneRepairAreaBySlug = (slug: string): PhoneRepairArea | undefined =>
  PHONE_REPAIR_AREAS.find(a => a.slug === slug);

export const PHONE_REPAIR_SLUGS = PHONE_REPAIR_AREAS.map(a => a.slug);

export const TEMECULA_REGION_AREAS = PHONE_REPAIR_AREAS.filter(a => a.region === 'temecula');
export const NORTH_COUNTY_SD_AREAS = PHONE_REPAIR_AREAS.filter(a => a.region === 'north-county-sd');
